import React, {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { Tab } from '@headlessui/react';
import * as yup from 'yup';
import { useParams } from 'react-router';
import { useFormik } from 'formik';

import '../Settings/setting.css';
import { Dish } from '../Menu/Dish';
import ModifierCategories from './ModifierCategories';
import { Language } from './Language';
import { Form } from '../../components/Form';
import {
    useCreateNewDish,
    useEditDish,
    useGetAllergens,
    useGetDish,
    useGetDishInputOptions, useGetDishLanguages, useGetSettings,
    useGetTax
} from '../../network';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { useUpdateDishAllergen } from '../../network';

const languagesOptions = [
    { value: 'hy', label: 'Armenian' },
    { value: 'en', label: 'English' },
    { value: 'ru', label: 'Russian' },
];

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

export const Dishes = () => {
    const { t } = useTranslation();
    const { dishId, catId } = useParams();
    const [pages, setPages] = useState([{ name: t('items'), path: `/chain/menu/${catId}/items` }]);

    const { data: dishData = {}, refetch } = useGetDish({ slug: dishId });
    const { data: dishOptions = {} } = useGetDishInputOptions({ slug: catId });
    const { data: { data: allergens = [] } = {} } = useGetAllergens();
    const tax = useGetTax();

    const { dish, modifierCategories = [], restaurants = [] } = dishData;

    const { mutate: createNewDish,isLoading: isCreatingNewDish } = useCreateNewDish({ refetch });
    const { mutate: editDish, isLoading: isEditingDish } = useEditDish();

    const { mutate: editAllergens } = useUpdateDishAllergen({ refetch });

    const { data: settings = {} } = useGetSettings();
    const { data: languages = {} } = useGetDishLanguages({ slug: dishId, enabled: !!dishId });

    const defaultLanguage = languagesOptions.find((lng) => lng.label === settings.language?.name) || { value: 'en', label: 'English' };

    const validationSchema = yup.object().shape({
        categories:  yup.array().min(1, t("Required!")),
        price: yup
            .string()
            .trim()
            .min(1, "Min price 1")
            .matches(/^\d+(\.\d{0,2})?$/, t("Must be only digits"))
            .required(t("Required!"))
    });

    const formik = useFormik({
        initialValues: {
            ...dish,
            title: dish?.title ?? '',
            description: dish?.description ?? '',
            price: dish?.price ?? '',
            total_price: dish?.total_price ?? 0,
            allergens: dish?.allergens ?? [],
            tags: dish?.tags ?? [],
            weight: dish?.weight ?? 0,
            calories: dish?.calories ?? 0,
            modifier_groups: dish?.modifier_groups || [],
            categories: dish?.categories || [],
            photo: '',
            tax_percent: dish?.tax_percent || 0,
            language: defaultLanguage.value,
            ...(languages?.data || {}),
        },
        enableReinitialize: true,
        validateOnChange: false,
        validationSchema: validationSchema,
        onSubmit: (data) => {
            const { restaurants, photo, ...dish } = data;
            if(!dishId) {
                createNewDish({ dish, photo });
            } else {
                editDish({ dish, photo });
                editAllergens({ dish });
            }
        },
    });


    const props = {
        loading: isEditingDish || isCreatingNewDish,
        dish,
        modifierCategories,
        tax,
        allergens,
        formik,
        dishOptions,
        editDish,
        createNewDish,
        defaultLanguage,
        restaurants
    };

    const tabs = [
        {
            name: `${t('General')}`,
            component: <Dish {...props} />,
        },
        {
            name: `${t('Modifiers')}`,
            component: <ModifierCategories {...props} />,
        },
        {
            name: `${t('Language')}`,
            component: <Language {...props} />,
        },
    ];

    useEffect(() => {
        if (!dishId) {
            setPages(() => [
                { name: t('items'), path: `/chain/menu/${catId}/items` },
                {
                    name: t("Create new dish"),
                    path: "/chain/menu/create",
                },
            ]);
        } else if (dishId && dish) {
            setPages(() => [
                { name: t('items'), path: `/chain/menu/${catId}/items` },
                {
                    name: dish.title || dish.name || "",
                    path: `/chain/menu/${catId}/dishes/${dishId}/edit`,
                },
            ]);
        }
    }, [dish]);

    return (
        <div className="max-w-screen-xl px-4 sm:px-8 lg:px-10 xl:px-12">
            <Breadcrumbs pages={pages} />
            <div className="w-full mt-10">
                <Tab.Group>
                    <Tab.List className="flex max-w-md rounded-lg shadow-md bg-gray-100">
                        {tabs.map(({ name }) => (
                            <Tab
                                key={name}
                                className={({ selected }) =>
                                    classNames(
                                        'w-full first:rounded-l-2xl tabs p-2 leading-5 text-xs font-medium',
                                        'ring-white font-extrabold focus:outline-none',
                                        selected ? 'bg-purple-500 f shadow-xl hover:text-white text-white' : 'text-black-700 hover:bg-indigo-100'
                                    )
                                }
                            >
                                {name}
                            </Tab>
                        ))}
                    </Tab.List>
                    <Tab.Panels className="mt-2 py-1 rounded-lg shadow-md bg-white">
                        <Form handleSubmit={formik.handleSubmit}>
                            {tabs.map((tab, idx) => (
                                <Tab.Panel key={idx} className={classNames('bg-white focus:outline-none')}>
                                    {tab.component}
                                </Tab.Panel>
                            ))}
                        </Form>
                    </Tab.Panels>
                </Tab.Group>
            </div>
        </div>
    );
};
